import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useId, useState, type ReactNode } from 'react'

import {
  ALL_TIME_FROM,
  WEEKDAYS_SHORT,
  addDays,
  firstOfMonth,
  firstOfYear,
  formatDayFull,
  formatDayShort,
  formatMonth,
  formatMonthShort,
  isIsoDay,
  lastOfMonth,
  monthsOfYearAll,
  spanDays,
  weekdayIndex,
} from './days'
import { centerScrollTarget } from './scrollCenter'
import './browse.css'

export interface DayRange {
  /** Первый день периода, `ГГГГ-ММ-ДД`. */
  from: string
  /** Последний день периода включительно. */
  to: string
}

/** Подсказка кнопки периода, который длиннее предела источника. */
const overLimitTitle = (maxDays: number | undefined): string => `Источник отдаёт не больше ${maxDays} дн. за раз`

/** Месяцы и все дни выбранного месяца; границы периода всегда видны. */
export function DayStrip({
  today,
  value,
  onChange,
  label = 'Период',
  maxDays,
  allTimeFrom = ALL_TIME_FROM,
  dataFrom = null,
  daysContent,
  customAside,
  pager = false,
  onMonth,
}: {
  /** Сегодняшний день человека, `ГГГГ-ММ-ДД`. */
  today: string
  value: DayRange
  onChange: (range: DayRange) => void
  label?: string
  /** Самый длинный период, который источник отдаёт за раз (у 1С — 31 день); `undefined` — без предела. Периоды длиннее недоступны. */
  maxDays?: number
  /** С какого дня начинается «За всё время»: у источника с ограниченной глубиной чтения — позже 2000 года. */
  allTimeFrom?: string
  /** Первый день, за который у источника есть данные: месяцы целиком до него серые и недоступны (там нулевые итоги). */
  dataFrom?: string | null
  daysContent?: ReactNode
  /** Справа от «От» и «До»: сведения о выбранном месяце (ожидаемая выручка). */
  customAside?: ReactNode
  /**
   * Режим вкладки «Чеки»: чеки читаются за один день, поэтому вместо месяцев года, «Весь год» и полей «От/До» — название месяца,
   * стрелки на предыдущий и следующий месяц и все дни показанного месяца. Нажатие на день выбирает только его.
   */
  pager?: boolean
  /**
   * Стрелки месяца в режиме `pager`: показанный месяц (`ГГГГ-ММ-01`) сообщается наружу — экрану, который читает данные за месяц
   * целиком (раздел «Смены»). Не передано — стрелки только листают дни, выбор не меняется.
   */
  onMonth?: (month: string) => void
}) {
  const [pagerMonth, setPagerMonth] = useState<string | null>(null)
  const months = monthsOfYearAll(today)
  const thisMonth = firstOfMonth(today)
  const monthEnd = lastOfMonth
  const latest = lastOfMonth(today)
  const fits = (from: string, to: string) => maxDays === undefined || spanDays(from, to) <= maxDays
  const yearStart = firstOfYear(today)

  const browse = isIsoDay(value.from) ? firstOfMonth(value.from) : thisMonth
  const fromId = useId()
  const toId = useId()
  const selectedMonth = months.find((month) => value.from === month && value.to === monthEnd(month))
  const thisYear = value.from === yearStart && value.to === today
  const allTime = value.from === allTimeFrom && value.to === today
  const days = Array.from({ length: spanDays(browse, lastOfMonth(browse)) }, (_, index) => addDays(browse, index))
  const choose = (range: DayRange) => {
    setPagerMonth(null)
    onChange(range)
  }

  const patch = (next: Partial<DayRange>) => {
    const merged = {
      from: next.from ?? (value.from || next.to || today),
      to: next.to ?? (value.to || next.from || today),
    }
    if (!isIsoDay(merged.from) || !isIsoDay(merged.to)) return
    let from = merged.from > latest ? latest : merged.from
    let to = merged.to > latest ? latest : merged.to
    if (from > to) {
      if (next.from !== undefined) to = from
      else from = to
    }
    if (maxDays !== undefined && !fits(from, to)) {
      if (next.from !== undefined) to = addDays(from, maxDays - 1)
      else from = addDays(to, 1 - maxDays)
    }
    onChange({ from, to })
  }

  const dayButton = (day: string) => {
    const future = day > today
    const pressed = day >= value.from && day <= value.to
    return (
      <button
        key={day}
        type="button"
        className="krp-day"
        aria-pressed={pressed}
        aria-label={`${WEEKDAYS_SHORT[weekdayIndex(day)]}, ${formatDayShort(day)}${day === today ? ', сегодня' : ''}${future ? ', ещё не наступил' : ''}`}
        disabled={future}
        title={future ? 'Этот день ещё не наступил' : formatDayFull(day)}
        data-today={day === today || undefined}
        onClick={() => choose({ from: day, to: day })}
      >
        <span className="krp-day__weekday">{WEEKDAYS_SHORT[weekdayIndex(day)]}</span>
        <span className="krp-day__date">{Number(day.slice(8, 10))}</span>
        {day === today && <span className="krp-day__today">сегодня</span>}
      </button>
    )
  }

  if (pager) {
    const shown = pagerMonth ?? browse
    const previousMonth = firstOfMonth(addDays(shown, -1))
    const nextMonth = addDays(lastOfMonth(shown), 1)
    const canPrevious = dataFrom == null || lastOfMonth(previousMonth) >= dataFrom
    const canNext = nextMonth <= today
    const flip = (month: string) => {
      setPagerMonth(month)
      onMonth?.(month)
    }
    return (
      <div className="krp-days krp-days--pager" role="group" aria-label={label}>
        <div className="krp-days__pager">
          <button type="button" className="krp-days__arrow" aria-label="Предыдущий месяц" title="Предыдущий месяц" disabled={!canPrevious} onClick={() => flip(previousMonth)}>
            <ChevronLeft size={18} aria-hidden="true" />
          </button>
          <strong className="krp-days__pager-title" aria-live="polite">{formatMonth(shown)}</strong>
          <button type="button" className="krp-days__arrow" aria-label="Следующий месяц" title="Следующий месяц" disabled={!canNext} onClick={() => flip(nextMonth)}>
            <ChevronRight size={18} aria-hidden="true" />
          </button>
        </div>
        <div className="krp-days__row" role="group" aria-label={`Дни: ${formatMonth(shown)}`}>
          {Array.from({ length: spanDays(shown, lastOfMonth(shown)) }, (_, index) => dayButton(addDays(shown, index)))}
        </div>
      </div>
    )
  }

  return (
    <div className="krp-days" role="group" aria-label={label}>
      <div className="krp-days__row">
        <div className="krp-days__months" role="group" aria-label="Месяцы года" ref={centerScrollTarget(browse)}>
          {months.map((month) => {
            const future = month > thisMonth
            const current = month === thisMonth
            const range = { from: month, to: monthEnd(month) }
            const allowed = fits(range.from, range.to)
            const noData = dataFrom !== null && range.to < dataFrom
            return (
              <button
                key={month}
                type="button"
                className="krp-day krp-day--month"
                aria-pressed={selectedMonth === month}
                aria-label={`${formatMonth(month)}${current ? ', текущий месяц' : ''}${future ? ', ещё не наступил' : ''}${noData ? ', данных нет' : ''}`}
                aria-current={browse === month ? 'true' : undefined}
                disabled={future || !allowed || noData}
                data-nodata={noData || undefined}
                title={future ? 'Этот месяц ещё не наступил' : noData ? `Данных за этот месяц нет: чеки в базе есть с ${formatDayFull(dataFrom ?? today)}` : allowed ? formatMonth(month) : overLimitTitle(maxDays)}
                data-today={current || undefined}
                data-browsed={browse === month || undefined}
                data-scroll-target={browse === month || undefined}
                onClick={() => choose(range)}
              >
                <span className="krp-day__date">{formatMonthShort(month)}</span>
                {current && <span className="krp-day__today">текущий</span>}
              </button>
            )
          })}
        </div>
        <button
          type="button"
          className="krp-day krp-day--wide"
          aria-pressed={thisYear}
          disabled={!fits(yearStart, today)}
          title={fits(yearStart, today) ? 'С 1 января по сегодня' : overLimitTitle(maxDays)}
          onClick={() => choose({ from: yearStart, to: today })}
        >
          Весь год
        </button>
        <button
          type="button"
          className="krp-day krp-day--wide"
          aria-pressed={allTime}
          disabled={!fits(allTimeFrom, today)}
          title={fits(allTimeFrom, today) ? 'Все чеки источника по сегодня' : overLimitTitle(maxDays)}
          onClick={() => choose({ from: allTimeFrom, to: today })}
        >
          За всё время
        </button>
      </div>

      {daysContent ?? <div className="krp-days__row" role="group" aria-label={`Дни: ${formatMonth(browse)}`}>
        {days.map(dayButton)}
      </div>}
      <div className="krp-days__custom">
        <div className="krp-field">
          <label className="krp-field__label" htmlFor={fromId}>
            От
          </label>
          <input id={fromId} type="date" max={latest} value={value.from} onChange={(event) => patch({ from: event.target.value })} />
        </div>
        <div className="krp-field">
          <label className="krp-field__label" htmlFor={toId}>
            До
          </label>
          <input id={toId} type="date" max={latest} value={value.to} onChange={(event) => patch({ to: event.target.value })} />
        </div>
        {customAside}
      </div>
    </div>
  )
}
